/**
 * WP 10.7 — the ticket line's wording, and the ticket check's pure shapes,
 * pulled out of `tickets.ts` because that file imports the Supabase client
 * and `@tanstack/react-query`, so nothing in it could be reached by the
 * offline suite (CLAUDE.md: put a decision in a dependency-free file and it
 * can be tested from the Worker's suite; `filmRank.ts`, `ticketRules.ts` and
 * `rowMeta.ts` are the same pattern — each defines its own copy of a shared
 * shape like `TicketStatus` rather than import one, since an import is the
 * one thing that would make it untestable again). No imports, by design.
 *
 * This closes two follow-ups at once:
 *
 * - **10.6 finding 5**: the chip's mark (`DATE_MARK`) and the summary line's
 *   word (`WORD`) were two separate object literals and drifted — the same
 *   `not_on_sale` status read "not on sale" on a chip and "not on sale yet"
 *   on the line. `DATE_MARK` is now derived from `WORD`, so they cannot
 *   disagree again; a test below asserts it structurally, not just today.
 * - **10.2's own follow-up** ("move `dateTicket` / `ticketLine` into a
 *   dependency-free file beside `ticketRules.ts` and test the precedence"):
 *   both functions, and everything they call, live here now. `tickets.ts`
 *   re-exports all of it, so nothing importing from `../lib/tickets` had to
 *   change.
 */

export type TicketStatus = 'available' | 'few' | 'sold_out' | 'not_on_sale' | 'unknown'

export interface TicketCheckResult {
  status: TicketStatus
  evidence: string | null
  source: 'rules' | 'model'
  checked_at: string
  url: string | null
  cached?: true
  /** T.3: why a check answered `unknown` — a closed set the Worker persists
   *  (`TicketReason` in `src/tickets.ts`, restated here because this file
   *  imports nothing). A string the app does not know reads as no reason. */
  reason?: TicketReason | string
  /** WP 10.4's per-date answer, where the page it read named its dates; T.3
   *  sends the tap's own fresh reading here, merged over the held rows. */
  dates?: { starts_at: string; status: TicketStatus; evidence: string | null }[]
}

export type TicketReason =
  | 'no_url' | 'host_not_allowed' | 'host_blocked_today' | 'blocked' | 'fetch_failed'
  | 'shop_only' | 'no_ticket_keyword' | 'per_date' | 'budget' | 'no_key' | 'bad_model'

/** T.3: the line for a check that learned no status, by why. The shop link
 *  follows every one (`unreadable`): each is a case where the page or the
 *  shop is what Doug needs next. Before this every miss read "couldn't tell
 *  from the page", including the ones where no page was ever read. */
export function reasonLine(reason: string | undefined, check: TicketCheckResult): string {
  switch (reason) {
    case 'shop_only':
    case 'host_not_allowed':
    case 'host_blocked_today':
      return 'Tickets: only the shop knows'
    case 'no_ticket_keyword':
      return "Tickets: the listing doesn't say"
    case 'fetch_failed':
    case 'blocked':
      return "Tickets: couldn't reach the page — try again"
    case 'per_date':
      // Every date the page named for this sheet came back known or not at
      // all; one that came back `unknown` is a date the page does not
      // describe, which is the listing not saying, not the date missing.
      return check.dates?.length ? "Tickets: the listing doesn't say" : 'Tickets: the page lists no upcoming date'
    default:
      return "Tickets: couldn't tell from the page"
  }
}

export type Known = Exclude<TicketStatus, 'unknown'>

const STATUSES: readonly string[] = ['available', 'few', 'sold_out', 'not_on_sale', 'unknown']
export const asStatus = (v: string | null | undefined): TicketStatus | null =>
  v && STATUSES.includes(v) ? (v as TicketStatus) : null

/** A known answer: `unknown` is a reading that found nothing, not a fact. */
export const isKnown = (s: TicketStatus | null | undefined): s is Known => !!s && s !== 'unknown'

/** One date's status and where it came from. `status` null: nobody knows. */
export interface DateTicket {
  status: Known | null
  checked_at: string | null
  source: string | null
}

/** One showing's ticket columns (10.0), as `showing_list` carries them. */
export interface ShowingTicket extends DateTicket {
  entry_id: string
  starts_at: string
}

/** Same instant, whatever the two strings' offsets: `showing_list` says
 *  `+00:00`, a Worker's JSON may say `Z`. */
const sameInstant = (a: string, b: string) => Date.parse(a) === Date.parse(b)

/** The summary line's word for each status — Doug reads this one standing
 *  alone, so it carries the fuller phrasing. The single place either word
 *  is written down; `DATE_MARK` below reads it rather than repeating it. */
export const WORD: Record<Known, string> = {
  available: 'available', few: 'few left', sold_out: 'sold out', not_on_sale: 'not on sale yet',
}

/** The mark a date chip carries, in the line's own words (10.6 finding 5:
 *  it used to be a second, disagreeing copy). Nothing for `available`: a
 *  mark on every open date is a mark on none. */
export const DATE_MARK: Partial<Record<Known, string>> = {
  sold_out: WORD.sold_out, few: WORD.few, not_on_sale: WORD.not_on_sale,
}

/**
 * Each date's status, freshest first: the tap's own `dates` (10.4) where it
 * named this date, else the row `showings` holds.
 */
export function dateTicket(
  d: { entry_id: string; starts_at: string },
  held: ShowingTicket[],
  check: TicketCheckResult | undefined,
): DateTicket {
  const tapped = check?.dates?.find(t => sameInstant(t.starts_at, d.starts_at))
  if (check && tapped && isKnown(tapped.status)) {
    return { status: tapped.status, checked_at: check.checked_at, source: check.source }
  }
  const row = held.find(h => h.entry_id === d.entry_id && sameInstant(h.starts_at, d.starts_at))
  return row ?? { status: null, checked_at: null, source: null }
}

/** The calendar day in `tz` — `zone.ts`'s `dayIn`, copied because this file
 *  imports nothing (above). */
const dayIn = (d: Date, tz: string) => d.toLocaleDateString('en-CA', { timeZone: tz })

/** "today", "yesterday", "3 days ago" — in the reader's days (`tz`, the
 *  current region's zone), not 24-hour blocks, so a check at 23:50 is
 *  "yesterday" ten minutes later. */
function ageInWords(at: string, now: Date, tz: string): string {
  const days = Math.round((Date.parse(dayIn(now, tz)) - Date.parse(dayIn(new Date(at), tz))) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

/** Where a status came from: the harvest read the house's own page; any
 *  other source was a tap. */
const fromWords = (source: string | null) => (source === 'harvest' ? 'the page' : 'a check')

const newer = (a: DateTicket, b: DateTicket) => ((a.checked_at ?? '') >= (b.checked_at ?? '') ? a : b)

/**
 * The one line under the date chips. `unreadable` is the one case with a
 * link after it: a check that learned no status, whose sentence says why
 * (`reasonLine`), so the shop or the page is what Doug needs.
 *
 * What it will not do is let a fresh tap that learned nothing erase a fact
 * the harvest already has: "couldn't tell" is the line only when nothing else
 * is known. `dates` is one entry per upcoming date the chips show; `entry` is
 * the row's own run-wide columns.
 */
export function ticketLine({ dates, entry, check, checking, failed, now, tz }: {
  dates: DateTicket[]
  entry: { ticket_status: string | null; ticket_checked_at: string | null; ticket_source: string | null }
  check: TicketCheckResult | undefined
  checking: boolean
  failed: boolean
  now: Date
  /** The current region's zone (C2.1): how long ago a check was is counted
   *  in the reader's days. */
  tz: string
}): { text: string; unreadable: boolean } {
  const line = (text: string, unreadable = false) => ({ text, unreadable })
  if (checking) return line('Tickets: checking…')

  const from = (t: DateTicket) =>
    t.checked_at ? ` · from ${fromWords(t.source)}, ${ageInWords(t.checked_at, now, tz)}` : ''

  const known = dates.filter((d): d is DateTicket & { status: Known } => isKnown(d.status))
  if (known.length > 0) {
    const n = dates.length
    const open = known.filter(d => d.status === 'available' || d.status === 'few').length
    const sold = known.filter(d => d.status === 'sold_out').length
    const notYet = known.filter(d => d.status === 'not_on_sale').length
    const unknown = n - known.length
    const what =
      n === 1 ? WORD[known[0].status]
      : unknown === 0 && open === n ? `all ${n} dates available`
      : unknown === 0 && sold === n ? `sold out on all ${n} dates`
      : unknown === 0 ? `${open} of ${n} dates available`
      : [open && `${open} available`, sold && `${sold} sold out`, notYet && `${notYet} not on sale yet`,
         `${unknown} not known`].filter(Boolean).join(', ')
    return line(`Tickets: ${what}${from(known.reduce<DateTicket>(newer, known[0]))}`)
  }

  // No date is known. The tap's own run-wide answer (the route as it is
  // deployed before 10.4) against the row's run-wide columns: the newer wins.
  const tapped: DateTicket | null = check && isKnown(check.status)
    ? { status: check.status, checked_at: check.checked_at, source: check.source } : null
  const heldStatus = asStatus(entry.ticket_status)
  const held: DateTicket | null = isKnown(heldStatus)
    ? { status: heldStatus, checked_at: entry.ticket_checked_at, source: entry.ticket_source } : null
  const best = tapped && held ? newer(tapped, held) : tapped ?? held
  if (best?.status) return line(`Tickets: ${WORD[best.status]}${from(best)}`)

  if (failed) return line("Tickets: couldn't check — try again")
  if (check) return line(reasonLine(check.reason, check), true)
  return line('Tickets: not checked')
}

/** Whether anything at all is known, so a sheet with no ticket kind and no
 *  harvested fact (a film, a gallery) draws no line. */
export const anyTicketFact = (dates: DateTicket[], entry: { ticket_status: string | null }) =>
  dates.some(d => isKnown(d.status)) || isKnown(asStatus(entry.ticket_status))

// ---------------------------------------------------------------------------
// Sold out, for the lists (a1-27)
// ---------------------------------------------------------------------------

/** One showing's identity in the sold-out set: the entry and the instant,
 *  the instant compared as a number (`sameInstant`'s reason — `+00:00` and
 *  `Z` are one time). */
export const showingKey = (entryId: string, startsAt: string): string => `${entryId}|${Date.parse(startsAt)}`

/**
 * Is this card sold out — the rule the lists hide by (a1-27, Doug's choice:
 * hide sold-out events from lists).
 *
 * Only when every future showing it has is marked sold out. A showing with no
 * mark, or any other mark, might be a night Doug can go to, so one of those
 * keeps the card — the rule never hides a card that may have a seat. Run
 * markers and annotations are not nights and are not counted. A card with no
 * future showing listed at all has only its own run-wide `ticket_status` to
 * go by, and is sold out when that says so; a card *with* showings ignores
 * the run-wide reading, which a date marked available would contradict.
 *
 * `soldOut` is the set of `showingKey`s marked `sold_out`; `showings` are the
 * card's own (every entry a set stands for). The sheet still says sold out —
 * this decides only whether a list draws the card.
 */
export function isSoldOut(
  card: { ticket_status?: string | null },
  showings: readonly { entry_id: string; starts_at: string; kind?: string | null }[],
  soldOut: ReadonlySet<string>,
  now: Date,
): boolean {
  const from = now.getTime()
  let nights = 0
  for (const s of showings) {
    if (s.kind === 'run' || s.kind === 'annotation') continue
    const t = Date.parse(s.starts_at)
    if (Number.isNaN(t) || t < from) continue
    nights++
    if (!soldOut.has(showingKey(s.entry_id, s.starts_at))) return false
  }
  if (nights > 0) return true
  return asStatus(card.ticket_status) === 'sold_out'
}
